<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="fixed inset-0 z-[9999]" @close="$emit('close')">
      <TransitionChild
        as="div"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
      />

      <div class="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          class="flex items-center justify-center w-full"
        >
          <DialogPanel
            class="relative transform rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all w-full max-w-xl max-h-[90vh] overflow-y-auto"
          >
            <button
              type="button"
              class="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              @click="$emit('close')"
            >
              <span class="sr-only">{{ $gettext("Fermer") }}</span>
              <XMarkIcon class="h-6 w-6" />
            </button>

            <template v-if="document">
              <DialogTitle class="text-base/7 font-semibold text-gray-900">
                {{ document.attributes.name }}
              </DialogTitle>

              <!-- Métadonnées (lecture) -->
              <div v-if="!editing" class="mt-1 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                <span>{{ documentTypeLabel }}</span>
                <span aria-hidden="true">·</span>
                <span>{{ categoryLabel }}</span>
                <span aria-hidden="true">·</span>
                <span>{{ revisionFrequencyLabel }}</span>
                <OrganizationDocumentStatusBadge :status="document.attributes.status" />
                <button
                  type="button"
                  class="text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110"
                  :title="$gettext('Modifier les informations')"
                  @click="startEditing"
                >
                  <PencilSquareIcon class="size-4" aria-hidden="true" />
                </button>
              </div>

              <!-- Métadonnées (édition) -->
              <form v-else class="mt-4 mb-6 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4" @submit.prevent="onSubmitMetadata">
                <div>
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Nom du document") }}</label>
                  <input
                    v-model="metadataForm.name"
                    type="text"
                    class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                  />
                  <p v-if="metadataErrors.name" class="mt-1 text-sm text-red-600">{{ metadataErrors.name }}</p>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Type de document") }}</label>
                    <select
                      v-model="metadataForm.documentType"
                      class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                    >
                      <option v-for="option in documentTypeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Catégorie") }}</label>
                    <select
                      v-model="metadataForm.category"
                      class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                    >
                      <option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Fréquence de révision") }}</label>
                  <select
                    v-model="metadataForm.revisionFrequency"
                    class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                  >
                    <option v-for="option in revisionFrequencyOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                  <input
                    v-if="metadataForm.revisionFrequency === 'other'"
                    v-model="metadataForm.revisionFrequencyNote"
                    type="text"
                    :placeholder="$gettext('Ex: À chaque changement législatif')"
                    class="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                  />
                </div>
                <p v-if="metadataErrors.base" class="text-sm text-red-600">{{ metadataErrors.base }}</p>
                <div class="flex justify-end gap-x-3">
                  <button
                    type="button"
                    class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    @click="editing = false"
                  >
                    {{ $gettext("Annuler") }}
                  </button>
                  <button
                    type="submit"
                    :disabled="savingMetadata"
                    class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {{ savingMetadata ? $gettext("Enregistrement...") : $gettext("Enregistrer") }}
                  </button>
                </div>
              </form>

              <!-- Nouvelle version -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-900">{{ $gettext("Déposer une nouvelle version") }}</h3>
                <FileDropzone v-model="newVersionFile" />
                <div class="flex items-end justify-between gap-4">
                  <div>
                    <label class="block text-sm/6 font-medium text-gray-900">{{ $gettext("Date de la version") }}</label>
                    <input
                      v-model="newVersionDate"
                      type="date"
                      class="mt-1 block rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                    />
                  </div>
                  <button
                    type="button"
                    :disabled="!newVersionFile || !newVersionDate || savingVersion"
                    class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    @click="onSubmitVersion"
                  >
                    {{ savingVersion ? $gettext("Dépôt...") : $gettext("Déposer la version") }}
                  </button>
                </div>
                <p v-if="versionErrors.base" class="text-sm text-red-600">{{ versionErrors.base }}</p>
              </div>

              <!-- Historique des versions -->
              <div class="mt-6">
                <h3 class="text-sm font-medium text-gray-900">{{ $gettext("Versions") }}</h3>
                <p class="mt-1 text-xs text-gray-500">{{ $gettext("L'historique des versions est immuable : une version déposée ne peut plus être supprimée.") }}</p>
                <ul role="list" class="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  <li v-for="version in sortedVersions" :key="version.id" class="flex items-center justify-between gap-3 px-4 py-3">
                    <div class="flex min-w-0 items-center gap-3">
                      <DocumentTextIcon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                      <div class="min-w-0">
                        <p class="truncate text-sm font-medium text-gray-900">{{ version.file.name }}</p>
                        <p class="text-xs text-gray-500">{{ version.versionDate }}</p>
                      </div>
                    </div>
                    <a
                      :href="version.file.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rounded-md p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      :title="$gettext('Télécharger')"
                    >
                      <ArrowDownTrayIcon class="size-4" aria-hidden="true" />
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon, PencilSquareIcon, DocumentTextIcon, ArrowDownTrayIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useOrganizationDocumentStore } from "@/stores/organizationDocument";
import FileDropzone from "@/components/ui/FileDropzone.vue";
import OrganizationDocumentStatusBadge from "./OrganizationDocumentStatusBadge.vue";
import { documentTypeLabels, categoryLabels, revisionFrequencyLabels, toOptions } from "./organizationDocumentLabels";

const { $gettext } = useGettext();

const props = defineProps({
  open: { type: Boolean, required: true },
  documentId: { type: String, default: null },
});
defineEmits(["close"]);

const organizationDocumentStore = useOrganizationDocumentStore();

// Read the document from the store so the modal refreshes after fetchAll.
const document = computed(() =>
  organizationDocumentStore.all.find((doc) => doc.id === props.documentId)
);

const documentTypeOptions = computed(() => toOptions(documentTypeLabels($gettext)));
const categoryOptions = computed(() => toOptions(categoryLabels($gettext)));
const revisionFrequencyOptions = computed(() => toOptions(revisionFrequencyLabels($gettext)));

const documentTypeLabel = computed(() => documentTypeLabels($gettext)[document.value?.attributes.documentType]);
const categoryLabel = computed(() => categoryLabels($gettext)[document.value?.attributes.category]);
const revisionFrequencyLabel = computed(() => {
  const attributes = document.value?.attributes;
  if (!attributes) return "";
  if (attributes.revisionFrequency === "other" && attributes.revisionFrequencyNote) {
    return attributes.revisionFrequencyNote;
  }
  return revisionFrequencyLabels($gettext)[attributes.revisionFrequency];
});

const sortedVersions = computed(() =>
  [...(document.value?.attributes.versions || [])].sort((a, b) => b.versionDate.localeCompare(a.versionDate))
);

// --- Édition des métadonnées ---
const editing = ref(false);
const savingMetadata = ref(false);
const metadataErrors = reactive({});
const metadataForm = reactive({
  name: "",
  documentType: "",
  category: "",
  revisionFrequency: "",
  revisionFrequencyNote: "",
});

const startEditing = () => {
  const attributes = document.value.attributes;
  metadataForm.name = attributes.name;
  metadataForm.documentType = attributes.documentType;
  metadataForm.category = attributes.category;
  metadataForm.revisionFrequency = attributes.revisionFrequency;
  metadataForm.revisionFrequencyNote = attributes.revisionFrequencyNote || "";
  Object.keys(metadataErrors).forEach((key) => delete metadataErrors[key]);
  editing.value = true;
};

const onSubmitMetadata = async () => {
  Object.keys(metadataErrors).forEach((key) => delete metadataErrors[key]);
  if (!metadataForm.name) metadataErrors.name = $gettext("est obligatoire");
  if (metadataForm.revisionFrequency === "other" && !metadataForm.revisionFrequencyNote) metadataErrors.base = $gettext("veuillez préciser la fréquence de révision");
  if (Object.keys(metadataErrors).length > 0) return;

  savingMetadata.value = true;

  await organizationDocumentStore.update(
    {
      id: props.documentId,
      type: "organizationDocument",
      attributes: {
        name: metadataForm.name,
        documentType: metadataForm.documentType,
        category: metadataForm.category,
        revisionFrequency: metadataForm.revisionFrequency,
        revisionFrequencyNote: metadataForm.revisionFrequencyNote,
      },
    },
    () => {
      savingMetadata.value = false;
      editing.value = false;
    },
    (errors) => {
      savingMetadata.value = false;
      if (errors.value) {
        Object.assign(metadataErrors, errors.value);
      }
    }
  );
};

// --- Nouvelle version ---
const today = () => new Date().toISOString().split("T")[0];
const newVersionFile = ref(null);
const newVersionDate = ref(today());
const savingVersion = ref(false);
const versionErrors = reactive({});

const onSubmitVersion = async () => {
  Object.keys(versionErrors).forEach((key) => delete versionErrors[key]);
  savingVersion.value = true;

  const formData = new FormData();
  formData.append("version[file]", newVersionFile.value);
  formData.append("version[version_date]", newVersionDate.value);

  await organizationDocumentStore.addVersion(
    props.documentId,
    formData,
    () => {
      savingVersion.value = false;
      newVersionFile.value = null;
      newVersionDate.value = today();
    },
    (errors) => {
      savingVersion.value = false;
      if (errors.value) {
        Object.assign(versionErrors, errors.value);
      }
    }
  );
};

watch(() => props.open, (val) => {
  if (!val) {
    editing.value = false;
    newVersionFile.value = null;
    newVersionDate.value = today();
    Object.keys(versionErrors).forEach((key) => delete versionErrors[key]);
    Object.keys(metadataErrors).forEach((key) => delete metadataErrors[key]);
  }
});
</script>
